import { Component, Inject, NgZone, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { firstValueFrom, Subscription, takeUntil, take,} from 'rxjs';
import { VideoCallService } from 'src/app/core/services/videocall.service';
import { Attention } from 'src/app/shared/models/attention';
import { EvolutionForm } from 'src/app/shared/models/evolutionForm';
import { Case } from 'src/app/shared/models/case';
import { HttpErrorResponse } from '@angular/common/http';
import * as OT from '@opentok/client';
import { OnInit } from '@angular/core';
import { CasesFacade } from 'src/app/abstraction/cases.facade';
import { JsonFormWizardComponent } from '../evolution-form/json-form-wizard/json-form-wizard.component';
import { MatIconModule } from '@angular/material/icon';
import { FormValues } from 'src/app/shared/models/evolutionForm';
import { MessageDialogComponent} from '../components/message-dialog/message-dialog.component';

export const videocallDraftKey = (attentionId: number | string): string => `videocall-form-draft-${attentionId}`;

type CallState =
    | 'connecting' | 'waiting' | 'live' | 'patient-video-off' | 'unstable'
    | 'reconnecting' | 'patient-left' | 'patient-lost' | 'ended' | 'failed';

const MEDIA_DENIED = 'No diste permiso para usar la cámara o el micrófono. Habilitalos desde el ícono del candado en la barra del navegador y reintentá.';
const NETWORK_ERROR = 'No se pudo conectar a la videollamada. Revisá tu conexión a internet y reintentá.';
const LINK_EXPIRED = 'El enlace de la videollamada expiró. Cerrá esta ventana y volvé a iniciar la videollamada.';

const CALL_ERRORS: Record<string, string> = {
    OT_USER_MEDIA_ACCESS_DENIED: MEDIA_DENIED,
    OT_PERMISSION_DENIED: MEDIA_DENIED,
    OT_REQUESTED_DEVICE_PERMISSION_DENIED: MEDIA_DENIED,
    OT_HARDWARE_UNAVAILABLE: 'La cámara o el micrófono están siendo usados por otra aplicación. Cerrala y reintentá.',
    OT_NO_DEVICES_FOUND: 'No se encontró cámara ni micrófono. Conectá un dispositivo y reintentá.',
    OT_UNABLE_TO_CAPTURE_MEDIA: 'No se pudo acceder a la cámara o al micrófono. Revisá que estén conectados y reintentá.',
    OT_CONNECT_FAILED: NETWORK_ERROR,
    OT_NOT_CONNECTED: NETWORK_ERROR,
    OT_DISCONNECTED: NETWORK_ERROR,
    OT_TIMEOUT: NETWORK_ERROR,
    OT_AUTHENTICATION_ERROR: LINK_EXPIRED,
    OT_INVALID_SESSION_ID: LINK_EXPIRED,
    OT_UNSUPPORTED_BROWSER: 'Tu navegador no es compatible con videollamadas. Usá Chrome, Edge, Firefox o Safari actualizados.',
};

const toCallErrorMessage = (error: any): string =>
    CALL_ERRORS[error?.name] ?? 'Ocurrió un error en la videollamada. Reintentá y, si persiste, recargá la página.';

@Component({
    selector: 'app-dialog-videocall',
    standalone: true,
    imports: [CommonModule, ClipboardModule, MatDialogModule, MatButtonModule, JsonFormWizardComponent, MatIconModule],
    templateUrl: './dialog-videocall.component.html',
    styleUrls: ['./dialog-videocall.component.scss'],
})
export class DialogVideoCallComponent implements OnInit, OnDestroy{

    @ViewChild('subscriberContainer')
    subscriberContainer: ElementRef<HTMLDivElement>;

    @ViewChild('publisherContainer')
    publisherContainer: ElementRef<HTMLDivElement>;

    @ViewChild(JsonFormWizardComponent)
    wizard?: JsonFormWizardComponent;

    @ViewChild('callContent')
    set callContent(element: ElementRef<HTMLElement> | undefined) {
        this.contentObserver?.disconnect();
        this.stopResize();
        this.contentElement = element?.nativeElement;
        if (!this.contentElement) return;
        this.contentObserver = new ResizeObserver(() => {
            this.zone.run(() => this.updatePanelBounds());
        });
        this.contentObserver.observe(this.contentElement);
    }

    readonly minFormWidth = 360;
    formWidth = 410;
    maxFormWidth = 410;
    panelsStacked = window.innerWidth <= 1200;
    resizing = false;
    private contentElement?: HTMLElement;
    private contentObserver?: ResizeObserver;
    private resizeHandle?: HTMLElement;
    private resizePointerId: number | null = null;
    private resizeStartX = 0;
    private resizeStartWidth = 410;

    initialValues: FormValues | null = null;
    private closing = false;

    confirmedForm: { formType: string; values: FormValues } | null = null;
    confirmedSummary: { title: string; items: { title: string; answer: string }[] }[] = [];

    callStarted = false;
    callState: CallState = 'connecting';
    connecting = false;
    micOn = true;
    cameraOn = true;
    _case: Subscription;
    case: Case | null = null;
    formHidden = false;
    patientStreamReady = false;

    callDurationSeconds = 0;
    private callTimerId: ReturnType<typeof setInterval> | null = null;

    private otSession: any;
    private publisher: any;
    private subscriber: any;
    private attempt = 0;

    session: Awaited<ReturnType<VideoCallService['createSession']>> | null = null;
    busy = true;
    error = '';
    constructor(
        private video: VideoCallService,
        private ref: MatDialogRef<DialogVideoCallComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { attention: Attention; form: EvolutionForm },
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
        private casesFacade: CasesFacade,
        private dialog: MatDialog,
    ) {}
    async ngOnInit(): Promise<void> {
        try {
            const saved = sessionStorage.getItem(videocallDraftKey(this.data.attention.id));
            this.initialValues = saved ? JSON.parse(saved).values ?? null : null;
        } catch {
            this.initialValues = null;
        }

        this._case = this.casesFacade.getCase$().subscribe(acase => {
            if (!!acase) {
                this.case = acase
            }
        })
        try {
            this.session = await this.video.createSession(
                this.data.attention.id
            );
        } catch (error: unknown) {
            if (error instanceof HttpErrorResponse) {
                console.error('Error HTTP al preparar videollamada', {
                    status: error.status,
                    url: error.url,
                });
            } else {
                console.error(
                    'Error al preparar videollamada',
                    error instanceof Error ? error.message : 'Error desconocido'
                );
            }
            this.error = 'No se pudo preparar la videollamada.';
        } finally {
            this.busy = false;
        }
    }

    async cancel(): Promise<void> {
        this.busy = true;
        this.error = '';
        try {
            if (this.session) await firstValueFrom(this.video.disableSession(this.session.sessionId));
            this.ref.close();
        } catch { this.error = LINK_EXPIRED; }
        finally { this.busy = false; }
    }

    continueToCall(): void {
        if (!this.session || this.connecting) return;

        if (typeof OT.initSession !== 'function'
            || typeof OT.initPublisher !== 'function') {
            this.error = 'El SDK de video no se cargó correctamente.';
            return;
        }
        if (!this.callStarted) {
            this.callStarted = true;
            this.ref.addPanelClass('videocall-expanded');
            this.ref.updateSize('100vw', '93dvh');
            this.ref.updatePosition({ bottom: '0', left: '0' });
            // Crea los contenedores del video antes de que OpenTok los use.
            this.cdr.detectChanges();
        }
        this.connect();
    }

    private connect(): void {
        this.error = '';
        this.connecting = true;
        this.patientStreamReady = false;
        this.callState = 'connecting';
        this.startCallTimer();

        // OT.initSession devuelve el mismo objeto para el mismo sessionId.
        const otSession = OT.initSession(this.session!.apiKey, this.session!.sessionId);
        const attempt = ++this.attempt;
        const isCurrent = () => attempt === this.attempt;
        this.otSession = otSession;
        const on = (name: string, handler: (event: any) => void) =>
            otSession.on(name, (event: any) => this.zone.run(() => {
                if (isCurrent()) handler(event);
            }));

        on('sessionReconnecting', () => this.callState = 'reconnecting');
        on('sessionReconnected', () => this.callState = this.subscriber ? this.liveState() : 'waiting');
        on('sessionDisconnected', (event) => {
            if (event.reason === 'networkDisconnected') this.error = NETWORK_ERROR;
            this.hangUp(event.reason === 'networkDisconnected' ? 'failed' : 'ended');
        });
        on('streamCreated', (event) => this.subscribe(event.stream));
        on('streamDestroyed', (event) => {
            this.subscriber = null;
            this.patientStreamReady = false;
            this.callState = event.reason === 'networkDisconnected' ? 'patient-lost' : 'patient-left';
        });

        otSession.connect(this.session!.token, (error: any) => this.zone.run(() => {
            if (!isCurrent()) return;
            this.connecting = false;
            if (error) return this.fail(error);
            if (this.callState === 'connecting') this.callState = 'waiting';

            this.publisher = OT.initPublisher(
                this.publisherContainer.nativeElement,
                {
                    insertMode: 'append',
                    width: '100%',
                    height: '100%',
                    fitMode: 'cover',
                    publishAudio: this.micOn,
                    publishVideo: this.cameraOn,
                    resolution: '640x480',
                    frameRate: 15,
                    style: { buttonDisplayMode: 'off', nameDisplayMode: 'off' },
                },
                (publisherError: any) => this.zone.run(() => {
                    if (publisherError) return this.fail(publisherError);
                    if (this.publisher?.session) return;
                    otSession.publish(this.publisher, (publishError: any) => {
                        if (publishError) this.zone.run(() => isCurrent() && this.fail(publishError));
                    });
                })
            );
        }));
    }

    private subscribe(stream: any): void {
        const attempt = this.attempt;
        const subscriber = this.otSession.subscribe(
            stream,
            this.subscriberContainer.nativeElement,
            { insertMode: 'append', width: '100%', height: '100%', fitMode: 'contain' },
            (error: any) => this.zone.run(() => {
                if (attempt !== this.attempt) return;
                if (error) {
                    console.error('Error al suscribirse al video del paciente', error);
                    this.error = toCallErrorMessage(error);
                    return;
                }
                this.subscriber = subscriber;
                this.patientStreamReady = true;
                this.callState = this.liveState();
            })
        );
        const on = (name: string, handler: (event: any) => void) =>
            subscriber.on(name, (event: any) => this.zone.run(() => {
                if (subscriber === this.subscriber) handler(event);
            }));

        on('videoDisabled', (event) => {
            if (event.reason === 'quality') this.callState = 'unstable';
            if (event.reason === 'publishVideo') this.callState = 'patient-video-off';
        });
        on('videoEnabled', () => this.callState = 'live');
        on('disconnected', () => this.callState = 'unstable');
        on('connected', () => this.callState = this.liveState());
    }

    private liveState(): CallState {
        return this.subscriber?.stream?.hasVideo === false ? 'patient-video-off' : 'live';
    }

    private fail(error: any): void {
        console.error('Error en la videollamada', error);
        this.error = toCallErrorMessage(error);
        this.hangUp('failed');
        this.tryContinueToEvolution();
    }

    @HostListener('window:resize')
    updatePanelBounds(): void {
        if (!this.contentElement) return;
        const width = this.contentElement.getBoundingClientRect().width;
        this.maxFormWidth = Math.max(this.minFormWidth, Math.floor(width - 342));
        this.panelsStacked = window.innerWidth <= 1200 || width < 702;
        if (this.panelsStacked) {
            this.stopResize();
        } else {
            this.setFormWidth(this.formWidth);
        }
        this.cdr.markForCheck();
    }

    startResize(event: PointerEvent): void {
        if (!event.isPrimary || event.button !== 0 || this.resizePointerId !== null) return;
        this.updatePanelBounds();
        if (this.panelsStacked) return;
        event.preventDefault();
        this.resizeHandle = event.currentTarget as HTMLElement;
        this.resizeHandle.setPointerCapture(event.pointerId);
        this.resizePointerId = event.pointerId;
        this.resizeStartX = event.clientX;
        this.resizeStartWidth = this.formWidth;
        this.resizing = true;
    }

    moveResize(event: PointerEvent): void {
        if (event.pointerId !== this.resizePointerId) return;
        this.setFormWidth(this.resizeStartWidth + this.resizeStartX - event.clientX);
    }

    stopResize(event?: PointerEvent): void {
        if (event && event.pointerId !== this.resizePointerId) return;
        const handle = this.resizeHandle;
        const pointerId = this.resizePointerId;
        this.resizing = false;
        this.resizePointerId = null;
        this.resizeHandle = undefined;
        if (pointerId !== null && handle?.hasPointerCapture(pointerId)) {
            handle.releasePointerCapture(pointerId);
        }
    }

    resizeWithKeyboard(event: KeyboardEvent): void {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        this.updatePanelBounds();
        if (this.panelsStacked) return;
        event.preventDefault();
        const step = event.shiftKey ? 50 : 10;
        const width = event.key === 'Home' ? this.minFormWidth
            : event.key === 'End' ? this.maxFormWidth
            : this.formWidth + (event.key === 'ArrowLeft' ? step : -step);
        this.setFormWidth(width);
    }

    private setFormWidth(width: number): void {
        this.formWidth = Math.round(Math.max(this.minFormWidth, Math.min(this.maxFormWidth, width)));
    }

    toggleMicrophone(): void {
        this.micOn = !this.micOn;
        this.publisher?.publishAudio(this.micOn);
    }

    toggleCamera(): void {
        this.cameraOn = !this.cameraOn;
        this.publisher?.publishVideo(this.cameraOn);
    }

    endCall(): void {
        this.hangUp('ended');
        this.tryContinueToEvolution();
    }

    private hangUp(state: 'ended' | 'failed'): void {
        const otSession = this.otSession;
        this.attempt++;
        this.otSession = null;
        if (otSession && this.publisher?.session) otSession.unpublish(this.publisher);
        this.publisher?.destroy();
        this.publisher = null;
        this.subscriber = null;
        if (otSession) {
            // OpenTok cachea la sesión; sin off() los handlers se acumulan al volver a llamar.
            otSession.off();
            otSession.disconnect();
            this.saveDraft();
        }
        this.stopCallTimer();
        this.connecting = false;
        this.patientStreamReady = false;
        this.callState = state;
    }

    ngOnDestroy(): void {
        this.hangUp('ended');
        this.stopResize();
        this.contentObserver?.disconnect();
        this._case?.unsubscribe();
    }

    get isCallActive(): boolean {
        return this.callState !== 'ended' && this.callState !== 'failed';
    }

    onFormSubmitted(result: { formType: string; values: FormValues }): void {
        this.confirmedForm = structuredClone(result);
        const sections = new Map<number, { title: string; items: { title: string; answer: string }[] }>();
        Object.values(result.values).forEach(item => {
            const stepNumber = item.step?.number ?? 0;
            if (!sections.has(stepNumber)) sections.set(stepNumber, { title: item.step?.title ?? '', items: [] });
            sections.get(stepNumber)!.items.push({ title: item.title, answer: this.formatSummaryAnswer(item.value) });
        });
        this.confirmedSummary = [...sections.entries()]
            .sort(([firstNumber], [secondNumber]) => firstNumber - secondNumber)
            .map(([, section]) => section);
        this.saveDraft();
        this.tryContinueToEvolution();
    }

    private formatSummaryAnswer(value: any): string {
        if (Array.isArray(value)) return value.map(item => this.formatSummaryAnswer(item)).join(', ');
        if (value !== null && typeof value === 'object') return value.label ?? value.value ?? '';
        if (typeof value === 'boolean') return value ? 'Sí' : 'No';
        return String(value ?? '');
    }

    private tryContinueToEvolution(): void {
        if (this.closing || this.isCallActive || !this.confirmedForm) return;
        this.closeWithForm(this.confirmedForm);
    }

    private closeWithForm(result: { formType: string; values: FormValues }): void {
        if (this.closing) return;

        this.closing = true;
        this.hangUp('ended');

        sessionStorage.setItem(
            videocallDraftKey(this.data.attention.id),
            JSON.stringify(result),
        );

        if (this.session) {
            this.video.disableSession(this.session.sessionId).subscribe({
                error: () => console.error('No se pudo invalidar el enlace de videollamada'),
            });
        }

        this.ref.close(result);
    }

    editForm(): void {
        this.confirmedForm = null;
        this.confirmedSummary = [];

    }

    // Hands the form (complete or not) to the evolution dialog.
    close(): void {
        if (this.closing) return;
        this.closing = true;
        const draft = this.confirmedForm ?? this.wizard?.getDraft() ?? { formType: '', values: {} };
        this.hangUp('ended');
        if (draft) sessionStorage.setItem(videocallDraftKey(this.data.attention.id), JSON.stringify(draft));
        if (this.session) {
            this.video.disableSession(this.session.sessionId).subscribe({
                error: () => console.error('No se pudo invalidar el enlace de videollamada'),
            });
        }
        this.ref.close(draft);
    }

    requestClose(): void {
        const popup = this.dialog.open(MessageDialogComponent, {
            width: 'min(440px, calc(100vw - 32px))',
            maxWidth: 'calc(100vw - 32px)',
            panelClass: 'videocall-confirmation-panel',
            disableClose: true,
            data: {
                title: '¿Cerrar y continuar a evolución?',
                message: 'Se conservarán las respuestas, aunque estén incompletas. Si la llamada sigue activa, se finalizará.',
                closeText: 'Volver', confirmText: 'Cerrar y continuar',
            },
        });
        popup.componentInstance.ok.pipe(
            take(1), takeUntil(popup.afterClosed()),
        ).subscribe(accepted => {
            popup.close();
            if (accepted) {
                const result = this.confirmedForm ?? this.wizard?.getDraft()
                    ?? { formType: '', values: {} };

                this.closeWithForm(result);
            }
        });
    }

    requestDiscardForm(): void {
        const popup = this.dialog.open(MessageDialogComponent, {
            width: 'min(440px, calc(100vw - 32px))',
            maxWidth: 'calc(100vw - 32px)',
            panelClass: 'videocall-confirmation-panel',
            disableClose: true,
            data: {
                title: '¿Descartar el formulario?',
                message: 'Se borrarán las respuestas y vas a poder empezar de nuevo.',
                closeText: 'Volver',
                confirmText: 'Descartar',
            },
        });

        popup.componentInstance.ok.pipe(
            take(1),
            takeUntil(popup.afterClosed()),
        ).subscribe(accepted => {
            popup.close();

            if (!accepted) return;

            this.confirmedForm = null;
            this.confirmedSummary = [];
            this.initialValues = null;
            sessionStorage.removeItem(videocallDraftKey(this.data.attention.id));
            this.wizard?.reset();
        });
    }

    get patientName(): string {
        return this.case?.patient?.name ?? 'el paciente';
    }

    get overlayMessage(): string | null {
        switch (this.callState) {
            case 'patient-video-off': return `${this.patientName} apagó la cámara`;
            case 'unstable': return 'Conexión inestable…';
            case 'reconnecting': return 'Se perdió la conexión. Reconectando…';
            default: return null;
        }
    }

    get waitingMessage(): string {
        switch (this.callState) {
            case 'patient-left': return `${this.patientName} salió de la llamada`;
            case 'patient-lost': return `${this.patientName} perdió la conexión`;
            default: return `Esperando a que ${this.patientName} se una`;
        }
    }

    private saveDraft(): void {
        const draft = this.confirmedForm ?? this.wizard?.getDraft()
        if (draft) sessionStorage.setItem(videocallDraftKey(this.data.attention.id), JSON.stringify(draft));
    }

    get formattedCallDuration(): string {
    const hours = Math.floor(this.callDurationSeconds / 3600);
    const minutes = Math.floor((this.callDurationSeconds % 3600) / 60);
    const seconds = this.callDurationSeconds % 60;

    return [
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0'),
    ].join(':');
    }

    private startCallTimer(): void {
        this.stopCallTimer();
        this.callDurationSeconds = 0;
        this.callTimerId = setInterval(() => {
            this.callDurationSeconds += 1;
            if (this.callDurationSeconds % 10 === 0) this.saveDraft();
        }, 1000);
    }

    private stopCallTimer(): void {
            if (this.callTimerId) {
                clearInterval(this.callTimerId);
                this.callTimerId = null;
        }
    }

    hideForm(): void {
        this.stopResize();
        this.formHidden = true;
    }

    showForm(): void {
        this.formHidden = false;
    }
}
